import { useState, useEffect, useRef } from 'react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

let cachedPrompt: BeforeInstallPromptEvent | null = null;

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    cachedPrompt = e as BeforeInstallPromptEvent;
  });
}

export function useInstaller() {
  const [deferredPrompt, setDeferredPrompt] =
    useState<BeforeInstallPromptEvent | null>(null);
  const [canInstall, setCanInstall] = useState(false);
  const [platform, setPlatform] = useState('unknown'); // ios | chromium | firefox | standalone | unsupported

  useEffect(() => {
    const userAgent = window.navigator.userAgent.toLowerCase();
    const isIos = /iphone|ipad|ipod/.test(userAgent);
    const isStandalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      (window.navigator as any).standalone === true;

    const isFirefox = userAgent.includes('firefox');
    // Detect Chromium-based browsers by UA, not by beforeinstallprompt presence
    const isChromium =
      userAgent.includes('chrome') || userAgent.includes('chromium');

    if (isStandalone) {
      setPlatform('standalone');
      setCanInstall(false);
      return;
    }

    if (isChromium && !isFirefox) {
      setPlatform('chromium');

      // Use cached prompt if event already fired
      if (cachedPrompt) {
        setDeferredPrompt(cachedPrompt);
        setCanInstall(true);
      }

      // Also listen for future firings (e.g. after dismissal)
      const handleBeforeInstallPrompt = (e: Event) => {
        e.preventDefault();
        cachedPrompt = e as BeforeInstallPromptEvent;
        setDeferredPrompt(e as BeforeInstallPromptEvent);
        setCanInstall(true);
      };
      window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      return () => {
        window.removeEventListener(
          'beforeinstallprompt',
          handleBeforeInstallPrompt
        );
      };
    }

    if (isFirefox) {
      setPlatform('firefox');
      setCanInstall(true);
      return;
    }

    if (isIos) {
      setPlatform('ios');
      setCanInstall(true);
      return;
    }

    setPlatform('unsupported');
    setCanInstall(false);
    return;
  }, []);

  const promptInstall = async () => {
    if (!deferredPrompt) return;
    await deferredPrompt.prompt();
    const choice = await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    setCanInstall(false);
    return choice;
  };

  const handleInstall = async () => {
    if (platform === 'chromium' && promptInstall) {
      await promptInstall();
    } else if (platform === 'firefox') {
      alert(
        'To install this app:\n\n1. Tap the browser menu (three dots).\n2. Choose "Install" or "Add to Home Screen".'
      );
    } else if (platform === 'ios') {
      alert(
        'To install this app:\n\n1. Tap the Share button in Safari (bottom toolbar).\n2. Scroll down and tap "Add to Home Screen".'
      );
    } else {
      alert('This device or browser does not support installing this PWA.');
    }
  };

  return {
    canInstall,
    platform,
    handleInstall,
  };
}
